import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Minimize2, DollarSign, CalendarClock, ShieldCheck, Target, Phone, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import MarkdownMessage from "@/components/MarkdownMessage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) => {
  if (!d) return null;
  try {
    const date = new Date(d);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
};

const QuickSummaryCard = ({ summary }) => {
  if (!summary) return null;

  const scores = summary.credit_scores || {};
  const hasScores = scores.experian || scores.equifax || scores.transunion;
  const upcomingAppts = summary.upcoming_appointments || [];
  const responsibilities = summary.client_responsibilities || [];

  return (
    <div className="space-y-2 mb-2" data-testid="crm-quick-summary">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#15803d] to-[#22c55e] rounded-xl px-3 py-2.5 text-white">
        <p className="text-xs font-semibold tracking-wide uppercase opacity-80">Quick Summary</p>
        <p className="text-sm font-bold">{summary.client_name}</p>
      </div>

      {/* Scores */}
      {hasScores && (
        <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-[#22c55e]" />
            <span className="text-xs font-semibold text-slate-700">Credit Scores</span>
          </div>
          <div className="flex gap-3">
            {scores.experian && (
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-slate-900">{scores.experian}</p>
                <p className="text-[10px] text-slate-500">EXP</p>
              </div>
            )}
            {scores.equifax && (
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-slate-900">{scores.equifax}</p>
                <p className="text-[10px] text-slate-500">EQ</p>
              </div>
            )}
            {scores.transunion && (
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-slate-900">{scores.transunion}</p>
                <p className="text-[10px] text-slate-500">TU</p>
              </div>
            )}
          </div>
          {summary.score_goal && (
            <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-slate-100">
              <Target className="h-3 w-3 text-[#22c55e]" />
              <span className="text-[11px] text-slate-600">Goal: <strong>{summary.score_goal}</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Status Row */}
      <div className="flex gap-2">
        {(summary.lead_status || summary.loan_ready_stage) && (
          <div className="bg-white rounded-xl px-3 py-2 shadow-sm flex-1">
            <p className="text-[10px] text-slate-500 uppercase font-semibold">Status</p>
            <p className="text-xs font-bold text-slate-800 mt-0.5">{summary.loan_ready_stage || summary.lead_status}</p>
            {summary.current_round && <p className="text-[10px] text-slate-500 mt-0.5">Round {summary.current_round}</p>}
          </div>
        )}
        {summary.payment_status && (
          <div className="bg-white rounded-xl px-3 py-2 shadow-sm flex-1">
            <p className="text-[10px] text-slate-500 uppercase font-semibold">Payment</p>
            <p className={`text-xs font-bold mt-0.5 ${summary.payment_status === "Current" ? "text-green-600" : summary.payment_status === "Failed Payment" ? "text-red-600" : "text-slate-800"}`}>
              {summary.payment_status}
            </p>
          </div>
        )}
      </div>

      {/* Billing */}
      {summary.next_payment && (
        <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-1">
            <DollarSign className="h-3.5 w-3.5 text-[#22c55e]" />
            <span className="text-xs font-semibold text-slate-700">Next Payment</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-900">${summary.next_payment.amount}</span>
            <span className="text-xs text-slate-500">Due {formatDate(summary.next_payment.due_date)}</span>
          </div>
          {summary.total_balance_due > 0 && summary.total_balance_due !== summary.next_payment.amount && (
            <p className="text-[10px] text-slate-500 mt-1">Total balance: ${summary.total_balance_due}</p>
          )}
        </div>
      )}

      {/* Upcoming Appointments */}
      {upcomingAppts.length > 0 && (
        <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-1">
            <CalendarClock className="h-3.5 w-3.5 text-[#22c55e]" />
            <span className="text-xs font-semibold text-slate-700">Appointments</span>
          </div>
          {upcomingAppts.slice(0, 3).map((apt, i) => (
            <div key={i} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
              <span className="text-xs text-slate-800 truncate flex-1">{apt.title}</span>
              <span className="text-[10px] text-slate-500 ml-2 shrink-0">{formatDate(apt.start_time)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Responsibilities */}
      {responsibilities.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
          <p className="text-[10px] text-amber-700 uppercase font-semibold mb-1">Action Items</p>
          {responsibilities.slice(0, 4).map((r, i) => (
            <p key={i} className="text-[11px] text-amber-900 leading-relaxed">- {r}</p>
          ))}
        </div>
      )}

      {/* Case Agent */}
      {summary.case_agent && (
        <div className="bg-white rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
          <Phone className="h-3.5 w-3.5 text-[#22c55e]" />
          <div>
            <p className="text-xs text-slate-800 font-semibold">{summary.case_agent}</p>
            {summary.case_agent_phone && <p className="text-[10px] text-slate-500">{summary.case_agent_phone}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const ClientAIChat = ({ clientId, firstName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, summary]);

  useEffect(() => {
    if (isOpen && messages.length === 0 && !summary) {
      loadHistory();
      fetchSummary();
    }
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await authFetch(`${API}/clients/${clientId}/crm-summary`);
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch {} finally {
      setSummaryLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await authFetch(`${API}/clients/${clientId}/chat-history`);
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        if (data.messages?.length > 0) {
          setMessages(data.messages.map(m => ({
            role: m.role,
            content: m.content,
          })));
        }
      }
    } catch {}
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const res = await authFetch(`${API}/clients/${clientId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.session_id) setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection issue. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        data-testid="ai-chat-toggle"
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-[#22c55e] to-[#15803d] text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-transform duration-200"
        style={{ boxShadow: "0 8px 32px rgba(34,197,94,0.4)" }}
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
      style={{ width: "380px", height: "600px", boxShadow: "0 16px 64px rgba(0,0,0,0.2)" }}
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#15803d] to-[#22c55e] px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <MessageCircle className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">CFI Assistant</p>
            <p className="text-green-100 text-xs">{firstName}'s File</p>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1" data-testid="ai-chat-minimize">
            <Minimize2 className="h-4 w-4" />
          </button>
          <button onClick={() => { setIsOpen(false); setMessages([]); setSessionId(null); setSummary(null); }} className="text-white/80 hover:text-white p-1" data-testid="ai-chat-close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#f0f4f0]" style={{ scrollbarWidth: "thin" }}>
        {/* Quick Summary Card */}
        {summaryLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 text-[#22c55e] animate-spin" />
            <span className="text-xs text-slate-500 ml-2">Loading CRM data...</span>
          </div>
        )}
        {summary && <QuickSummaryCard summary={summary} />}

        {/* Welcome prompt after summary */}
        {messages.length === 0 && !loading && summary && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] shadow-sm">
              <p className="text-sm text-slate-800">
                Ask me anything about <strong>{firstName}</strong> — billing, scores, progress, game plan, appointments, or any credit questions.
              </p>
            </div>
          </div>
        )}

        {/* No summary, no history — basic welcome */}
        {messages.length === 0 && !loading && !summary && !summaryLoading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] shadow-sm">
              <p className="text-sm text-slate-800">
                Hi! Ask me anything about <strong>{firstName}</strong> — their scores, progress, game plan, or any credit questions.
              </p>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`px-4 py-2.5 max-w-[85%] text-sm shadow-sm ${
                msg.role === "user"
                  ? "bg-[#22c55e] text-white rounded-2xl rounded-br-md whitespace-pre-wrap"
                  : "bg-white text-slate-800 rounded-2xl rounded-bl-md"
              }`}
            >
              {msg.role === "assistant" ? (
                <MarkdownMessage>{msg.content}</MarkdownMessage>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-[#22c55e] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-[#22c55e] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-[#22c55e] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t shrink-0">
        <div className="flex items-center gap-2 bg-[#f0f4f0] rounded-full pl-4 pr-1 py-1">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask about ${firstName}...`}
            className="flex-1 bg-transparent text-sm outline-none placeholder-slate-400"
            disabled={loading}
            data-testid="ai-chat-input"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="bg-[#22c55e] text-white rounded-full p-2 hover:bg-[#15803d] transition-colors disabled:opacity-40"
            data-testid="ai-chat-send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientAIChat;
